import { createSlice, createAsyncThunk, SerializedError, miniSerializeError } from '@reduxjs/toolkit';
import { resetStore } from 'src/store';

import { ServerErrorAdapter } from '@itm/shared-frontend/lib/utils';

import { getDirectDebitContactDetails } from 'src/api/employerHub/directDebit';

import { DirectDebitContactDetailsResponse, ServerError } from 'src/types';

type DirectDebitContactDetailsState = {
  contactDetails: DirectDebitContactDetailsResponse | null;
  loadedCompanyId: string;
  isLoading: boolean;
  serverErrorMessages: string[];
};

const serializeError = (e: unknown): SerializedError => {
  const message = new ServerErrorAdapter(e as ServerError).combine().join('\n  ');
  return miniSerializeError({ message });
};

type FetchDirectDebitContactDetailsActionParams = { companyId: string };

export const fetchDirectDebitContactDetailsAction = createAsyncThunk(
  'directDebitContactDetails/fetch',
  async ({ companyId }: FetchDirectDebitContactDetailsActionParams) => {
    const res = await getDirectDebitContactDetails(companyId);
    return res.data;
  },
  {
    condition: ({ companyId }, { getState }) => {
      const { directDebitContactDetails } = getState() as { directDebitContactDetails: DirectDebitContactDetailsState };
      if (directDebitContactDetails.isLoading) return false;
      return directDebitContactDetails.loadedCompanyId !== companyId;
    },
    serializeError,
  },
);

const initialState: DirectDebitContactDetailsState = {
  contactDetails: null,
  loadedCompanyId: '',
  isLoading: false,
  serverErrorMessages: [],
};

const directDebitContactDetailsSlice = createSlice({
  name: 'directDebitContactDetails',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchDirectDebitContactDetailsAction.pending, (state: DirectDebitContactDetailsState) => {
      state.isLoading = true;
      state.serverErrorMessages = [];
    });
    builder.addCase(fetchDirectDebitContactDetailsAction.fulfilled, (state: DirectDebitContactDetailsState, action) => {
      state.isLoading = false;
      state.contactDetails = action.payload;
      state.loadedCompanyId = action.meta.arg.companyId;
    });
    builder.addCase(fetchDirectDebitContactDetailsAction.rejected, (state: DirectDebitContactDetailsState, action) => {
      state.isLoading = false;
      state.serverErrorMessages = action.error.message ? [action.error.message] : [];
    });

    // Cleanup
    builder.addCase(resetStore, (state: DirectDebitContactDetailsState) => {
      Object.assign(state, initialState);
    });
  },
});

export default directDebitContactDetailsSlice.reducer;
